import { Controller } from "@hotwired/stimulus"

/**
 * Thread Panel Controller
 * 
 * Manages the thread side-panel for viewing and replying to message threads.
 * Opens when user clicks reply on a message, loads thread via Turbo Frame.
 * 
 * Usage:
 *   <div data-controller="thread-panel"
 *        data-thread-panel-thread-url-template-value="/messages/:id/thread">
 *     <div data-thread-panel-target="panel" class="d-none">
 *       <turbo-frame id="thread-content-frame" data-thread-panel-target="frame">
 *       </turbo-frame>
 *     </div>
 *   </div>
 */
export default class extends Controller {
  static targets = ["panel", "frame", "repliesList"]
  static values = {
    open: { type: Boolean, default: false },
    parentMessageId: Number,
    threadUrlTemplate: { type: String, default: "/messages/:id/thread" }
  }

  connect() {
    // Listen for thread:open events dispatched from message reply buttons
    this.boundHandleOpen = this.handleOpen.bind(this)
    this.element.addEventListener("thread:open", this.boundHandleOpen)
  }

  disconnect() {
    this.element.removeEventListener("thread:open", this.boundHandleOpen)
  }

  handleOpen(event) {
    const messageId = event.detail?.messageId
    if (messageId) {
      this.openThread(messageId)
    }
  }

  open(event) {
    event.preventDefault()
    const messageId = event.currentTarget.dataset.messageId || 
                      event.params?.messageId
    if (messageId) {
      this.openThread(messageId)
    }
  }

  openThread(messageId) {
    this.parentMessageIdValue = parseInt(messageId, 10)
    
    // Build the thread URL
    const url = this.threadUrlTemplateValue.replace(":id", messageId)
    
    // Set the Turbo Frame src to load thread content
    if (this.hasFrameTarget) {
      this.frameTarget.src = url
    }
    
    // Show the panel
    this.openValue = true
    this.showPanel()
  }

  close() {
    this.openValue = false
    this.hidePanel()
    
    // Reset the frame src (stops loading) but don't clear innerHTML
    if (this.hasFrameTarget) {
      this.frameTarget.removeAttribute("src")
    }
  }

  showPanel() {
    if (this.hasPanelTarget) {
      this.panelTarget.classList.remove("d-none")
      this.panelTarget.classList.add("d-flex", "flex-column", "thread-panel-open")
    }
  }

  hidePanel() {
    if (this.hasPanelTarget) {
      this.panelTarget.classList.remove("d-flex", "flex-column", "thread-panel-open")
      this.panelTarget.classList.add("d-none")
    }
  }

  // Scroll to latest reply after a new one is added
  scrollToLatestReply() {
    if (this.hasRepliesListTarget) {
      const list = this.repliesListTarget
      requestAnimationFrame(() => {
        list.scrollTop = list.scrollHeight
      })
    }
  }

  // Called after Turbo Stream appends a new reply
  repliesListTargetConnected() {
    this.scrollToLatestReply()
  }

  // Handle turbo:submit-end from the thread composer
  handleSubmitEnd(event) {
    if (event.detail.success) {
      this.scrollToLatestReply()
    }
  }
}
